// ============================================================================
// Tasks — Monthly Operations data layer (mock + localStorage)
// ----------------------------------------------------------------------------
// Predisposto per Supabase: monthly_operations, monthly_operation_items,
// monthly_campaign_items, followups, extra_costs, gmail_connections.
// Nessuna API reale (Gmail/Meta) collegata.
// ============================================================================

import { CLIENTS, userById } from "@/data/mockData";
import { getRegistryClient } from "@/lib/clientRegistry";
import { api } from "@/lib/api";

export const ITEM_STATUS = {
  to_start: { label: "Da iniziare", tone: "neutral", dot: "bg-slate-400", chip: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20" },
  in_progress: { label: "In corso", tone: "info", dot: "bg-sky-500", chip: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20" },
  blocked: { label: "Bloccato", tone: "error", dot: "bg-red-500", chip: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20" },
  pending_review: { label: "In revisione", tone: "warning", dot: "bg-amber-500", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  waiting_client: { label: "In attesa cliente", tone: "special", dot: "bg-violet-500", chip: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20" },
  completed: { label: "Completato", tone: "ok", dot: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  approved: { label: "Approvato", tone: "ok", dot: "bg-green-600", chip: "bg-green-600/10 text-green-700 dark:text-green-400 border-green-600/20" },
};
export const ITEM_STATUS_ORDER = ["to_start", "in_progress", "blocked", "pending_review", "waiting_client", "completed", "approved"];

// Stati specifici per tipo attività (redesign Operazioni Mensili).
export const EDITORIAL_STATUS = {
  to_start: { label: "Da iniziare", dot: "bg-slate-400", chip: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20" },
  programmato: { label: "Programmato", dot: "bg-sky-500", chip: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20" },
  in_modifica: { label: "In modifica", dot: "bg-amber-500", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  pending_review: { label: "In attesa approvazione", dot: "bg-violet-500", chip: "bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20" },
  completed: { label: "Completato", dot: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  non_gestire: { label: "Non gestire questo mese", dot: "bg-muted-foreground/40", chip: "bg-muted/40 text-muted-foreground border-border/60" },
};
export const EDITORIAL_STATUS_ORDER = ["to_start", "programmato", "in_modifica", "pending_review", "completed", "non_gestire"];
export const CALL_STATUS = {
  to_program: { label: "Da programmare", dot: "bg-slate-400", chip: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/20" },
  scheduled: { label: "Agendata", dot: "bg-sky-500", chip: "bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20" },
  completed: { label: "Completato", dot: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  postponed: { label: "Rinviata", dot: "bg-amber-500", chip: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  cancelled: { label: "Annullata", dot: "bg-rose-500", chip: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20" },
};
export const CALL_STATUS_ORDER = ["to_program", "scheduled", "postponed", "completed", "cancelled"];

export const OBJECTIVES = ["Lead", "Acquisto", "Traffico", "Engagement"];
export const FOLLOWUP_STATUS = {
  draft: { label: "Bozza", tone: "neutral" },
  sent: { label: "Inviato", tone: "ok" },
  failed: { label: "Errore", tone: "error" },
};
export const FOLLOWUP_SOURCE = {
  monthly_call: "Riunione mensile",
  editorial_plan: "Piano editoriale",
  campaigns: "Campagne",
  extra_contact: "Contatto extra",
};

const now = () => new Date().toISOString();
export const monthKey = (y, m) => `${y}-${m}`;
export const MONTHS_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const read = (k, fb) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

// ---------------------------------------------------------------------------
// MONTHLY OPERATIONS — real FastAPI + MongoDB backend (Module C)
// ---------------------------------------------------------------------------
// Source of truth = backend `/api/operations` (RBAC + scope enforced server
// side; months seeded lazily). A per-month in-memory cache keeps the existing
// synchronous UI working; mutations are optimistic + persisted in background.
const _ops = {};            // { "2026-6": [op,...] }
const _hydrated = new Set();
const _opsSubs = new Set();
const notifyOps = () => _opsSubs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeOps = (fn) => { _opsSubs.add(fn); return () => _opsSubs.delete(fn); };
export const resetOps = () => { Object.keys(_ops).forEach((k) => delete _ops[k]); _hydrated.clear(); };

async function hydrateOps(y, m) {
  const key = monthKey(y, m);
  try {
    const { data } = await api.get("/operations", { params: { year: y, month: m } });
    _ops[key] = Array.isArray(data) ? data : [];
  } catch {
    _ops[key] = _ops[key] || [];
  } finally {
    _hydrated.add(key);
    notifyOps();
  }
}

export const listOps = (y, m) => {
  const key = monthKey(y, m);
  if (!_hydrated.has(key)) { if (!(key in _ops)) _ops[key] = []; _hydrated.add(key); hydrateOps(y, m); }
  return _ops[key] || [];
};

const opsOf = (y, m) => _ops[monthKey(y, m)] || [];
const replaceOp = (y, m, next) => {
  const key = monthKey(y, m);
  _ops[key] = opsOf(y, m).map((o) => (o.id === next.id ? next : o));
  notifyOps();
};
const resyncOps = (y, m) => { _hydrated.delete(monthKey(y, m)); hydrateOps(y, m); };

export const updateOpItem = (y, m, opId, itemId, patch) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), items: op.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) });
  api.patch(`/operations/${opId}/items/${itemId}`, patch).catch(() => resyncOps(y, m));
};

export const addOpCampaign = (y, m, opId) => {
  const tmp = `mci_tmp_${Date.now()}`;
  const op = opsOf(y, m).find((o) => o.id === opId);
  const camp = { id: tmp, campaign_name: "Nuova campagna", objective: "Lead", theme: "", front_end: "", status: "to_start", time_spent: 0, time_by_resource: {} };
  if (op) replaceOp(y, m, { ...op, updated_at: now(), campaigns: [...op.campaigns, camp] });
  api.post(`/operations/${opId}/campaigns`, {})
    .then(({ data }) => {
      const o2 = opsOf(y, m).find((o) => o.id === opId);
      if (o2) replaceOp(y, m, { ...o2, campaigns: o2.campaigns.map((c) => (c.id === tmp ? { ...c, id: data.id } : c)) });
    })
    .catch(() => resyncOps(y, m));
};

export const updateOpCampaign = (y, m, opId, campId, patch) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), campaigns: op.campaigns.map((c) => (c.id === campId ? { ...c, ...patch } : c)) });
  api.patch(`/operations/${opId}/campaigns/${campId}`, patch).catch(() => resyncOps(y, m));
};

export const removeOpCampaign = (y, m, opId, campId) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), campaigns: op.campaigns.filter((c) => c.id !== campId) });
  api.delete(`/operations/${opId}/campaigns/${campId}`).catch(() => resyncOps(y, m));
};

export const removeOpsFromMonth = (y, m, clientIds) => {
  const key = monthKey(y, m);
  _ops[key] = opsOf(y, m).filter((op) => !clientIds.includes(op.client_id));
  notifyOps();
  api.post("/operations/remove", { year: y, month: m, client_ids: clientIds }).catch(() => resyncOps(y, m));
};

export const copyOpsToMonth = async (fromY, fromM, toY, toM, clientIds, copyUpdates = false) => {
  const toKey = monthKey(toY, toM);
  const { data } = await api.post("/operations/copy", { from_year: fromY, from_month: fromM, to_year: toY, to_month: toM, client_ids: clientIds, copy_updates: copyUpdates });
  _hydrated.delete(toKey);
  if (toKey in _ops) await hydrateOps(toY, toM);
  return data;
};

// Inclusione manuale di clienti per il mese (anche occasionali). Rimuove l'esclusione.
export const addOpsToMonth = async (y, m, clientIds) => {
  const key = monthKey(y, m);
  await api.post("/operations/add", { year: y, month: m, client_ids: clientIds });
  _hydrated.delete(key);
  await hydrateOps(y, m);
};

// Helpers derivati
export const itemTime = (entity) => {
  const tbr = entity?.time_by_resource;
  if (tbr && Object.keys(tbr).length) return Object.values(tbr).reduce((s, v) => s + (Number(v) || 0), 0);
  return Number(entity?.time_spent) || 0;
};
export const campaignsTime = (op) => op.campaigns.reduce((s, c) => s + itemTime(c), 0);

// Registra le ore per singola risorsa (membro del team) su un item o campagna.
export const setItemResourceTime = (y, m, opId, itemId, userId, hours) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (!op) return;
  const it = op.items.find((i) => i.id === itemId);
  const tbr = { ...(it?.time_by_resource || {}) };
  if (Number(hours) > 0) tbr[userId] = Number(hours); else delete tbr[userId];
  const total = Object.values(tbr).reduce((s, v) => s + (Number(v) || 0), 0);
  replaceOp(y, m, { ...op, updated_at: now(), items: op.items.map((i) => (i.id === itemId ? { ...i, time_by_resource: tbr, time_spent: total } : i)) });
  api.patch(`/operations/${opId}/items/${itemId}`, { time_by_resource: tbr }).catch(() => resyncOps(y, m));
};
export const setCampaignResourceTime = (y, m, opId, campId, userId, hours) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (!op) return;
  const c0 = op.campaigns.find((c) => c.id === campId);
  const tbr = { ...(c0?.time_by_resource || {}) };
  if (Number(hours) > 0) tbr[userId] = Number(hours); else delete tbr[userId];
  const total = Object.values(tbr).reduce((s, v) => s + (Number(v) || 0), 0);
  replaceOp(y, m, { ...op, updated_at: now(), campaigns: op.campaigns.map((c) => (c.id === campId ? { ...c, time_by_resource: tbr, time_spent: total } : c)) });
  api.patch(`/operations/${opId}/campaigns/${campId}`, { time_by_resource: tbr }).catch(() => resyncOps(y, m));
};

export const opResourceHours = (op) => {
  const map = {};
  const team = op.assigned_team.length ? op.assigned_team : [];
  const addEntity = (e) => {
    const tbr = e.time_by_resource;
    if (tbr && Object.keys(tbr).length) {
      Object.entries(tbr).forEach(([u, h]) => { map[u] = (map[u] || 0) + (Number(h) || 0); });
    } else {
      const total = Number(e.time_spent) || 0;
      const share = team.length ? total / team.length : 0;
      team.forEach((u) => { map[u] = (map[u] || 0) + share; });
    }
  };
  op.items.filter((i) => i.item_type !== "campaigns").forEach(addEntity);
  op.campaigns.forEach(addEntity);
  return map;
};

export const opProgress = (op) => {
  const subs = op.items.map((it) => {
    if (it.item_type === "campaigns") {
      const t = op.campaigns.length;
      if (!t) return it.status === "completed" ? 1 : 0;
      return op.campaigns.filter((c) => c.status === "completed").length / t;
    }
    return it.status === "completed" ? 1 : 0;
  });
  return subs.length ? Math.round((subs.reduce((a, b) => a + b, 0) / subs.length) * 100) : 0;
};
export const extraTime = (op) => (op.extra_activities || []).reduce((s, e) => s + itemTime(e), 0);
export const opTotalTime = (op) =>
  op.items.filter((i) => i.item_type !== "campaigns").reduce((s, i) => s + itemTime(i), 0) +
  campaignsTime(op) + extraTime(op);

// Stato derivato del totale campagne: "completato" solo se TUTTE le campagne sono completate.
export const campaignsDerivedStatus = (op) => {
  const camps = op.campaigns || [];
  if (!camps.length) return "to_start";
  if (camps.every((c) => c.status === "completed" || c.status === "approved")) return "completed";
  // Se anche una sola campagna è oltre "da iniziare" (in corso, in revisione, ecc.) → in corso.
  if (camps.some((c) => c.status && c.status !== "to_start")) return "in_progress";
  return "to_start";
};

// Ripartizione degli stati delle campagne (per il tooltip del badge riepilogativo).
export const campaignsBreakdown = (op) => {
  const bd = {};
  (op.campaigns || []).forEach((c) => { const k = c.status || "to_start"; bd[k] = (bd[k] || 0) + 1; });
  return bd;
};

// Impostazioni di espansione per-cliente (quali attività mostrare + numero).
export const updateOpSettings = (y, m, opId, patch) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), settings: { ...(op.settings || {}), ...patch } });
  api.patch(`/operations/${opId}/settings`, patch).catch(() => resyncOps(y, m));
};

// Attività extra (descrizione + tempo) per cliente/mese.
export const addExtraActivity = (y, m, opId) => {
  const tmp = `mea_tmp_${Date.now()}`;
  const op = opsOf(y, m).find((o) => o.id === opId);
  const ea = { id: tmp, description: "", time_spent: 0, time_by_resource: {} };
  if (op) replaceOp(y, m, { ...op, updated_at: now(), extra_activities: [...(op.extra_activities || []), ea] });
  api.post(`/operations/${opId}/extra`, {})
    .then(({ data }) => { const o2 = opsOf(y, m).find((o) => o.id === opId); if (o2) replaceOp(y, m, { ...o2, extra_activities: (o2.extra_activities || []).map((e) => (e.id === tmp ? { ...e, id: data.id } : e)) }); })
    .catch(() => resyncOps(y, m));
};
export const updateExtraActivity = (y, m, opId, eaId, patch) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), extra_activities: (op.extra_activities || []).map((e) => (e.id === eaId ? { ...e, ...patch } : e)) });
  api.patch(`/operations/${opId}/extra/${eaId}`, patch).catch(() => resyncOps(y, m));
};
export const removeExtraActivity = (y, m, opId, eaId) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (op) replaceOp(y, m, { ...op, updated_at: now(), extra_activities: (op.extra_activities || []).filter((e) => e.id !== eaId) });
  api.delete(`/operations/${opId}/extra/${eaId}`).catch(() => resyncOps(y, m));
};
export const setExtraResourceTime = (y, m, opId, eaId, userId, hours) => {
  const op = opsOf(y, m).find((o) => o.id === opId);
  if (!op) return;
  const e0 = (op.extra_activities || []).find((e) => e.id === eaId);
  const tbr = { ...(e0?.time_by_resource || {}) };
  if (Number(hours) > 0) tbr[userId] = Number(hours); else delete tbr[userId];
  const total = Object.values(tbr).reduce((s, v) => s + (Number(v) || 0), 0);
  replaceOp(y, m, { ...op, updated_at: now(), extra_activities: op.extra_activities.map((e) => (e.id === eaId ? { ...e, time_by_resource: tbr, time_spent: total } : e)) });
  api.patch(`/operations/${opId}/extra/${eaId}`, { time_by_resource: tbr }).catch(() => resyncOps(y, m));
};

export const itemOf = (op, type) => op.items.find((i) => i.item_type === type);

export const monthKpis = (y, m, ops) => {
  const activeClients = ops.length;
  const totalCampaigns = ops.reduce((s, o) => s + o.campaigns.length, 0);
  const totalEditorial = ops.filter((o) => itemOf(o, "editorial_plan")).length;
  const hours = {};
  ops.forEach((o) => {
    const rh = opResourceHours(o);
    Object.entries(rh).forEach(([uid, h]) => { hours[uid] = (hours[uid] || 0) + h; });
  });
  const byMember = Object.entries(hours).map(([uid, h]) => ({ name: userById(uid)?.name?.split(" ")[0] || uid, hours: Math.round(h) }));
  return { activeClients, totalCampaigns, totalEditorial, byMember };
};

// ---------------------------------------------------------------------------
// FOLLOW-UPS (per cliente, tutti i mesi)
// ---------------------------------------------------------------------------
const FU_KEY = "aesthetic-os-followups";
// Follow-up ora persistiti su DB e condivisi da tutto il team (endpoint /operations/followups).
export const getFollowups = async (clientId) => {
  const { data } = await api.get("/operations/followups", { params: { client_id: clientId } });
  return data || [];
};
export const addFollowup = (record) =>
  api.post("/operations/followups", {
    client_id: record.client_id, year: record.year, month: record.month, source: record.source,
    meeting_notes: record.meeting_notes, prompt_used: record.prompt_used,
    email_subject: record.email_subject, email_body: record.email_body,
    sent_to_email: record.sent_to_email, status: record.status || "draft", send: !!record.send,
    ai_generated: !!record.ai_generated,
  }).then((r) => r.data);
export const patchFollowup = (id, fields) => api.patch(`/operations/followups/${id}`, fields).then((r) => r.data);
export const sendFollowupDraft = (id, fields) => api.post(`/operations/followups/${id}/send`, fields || {}).then((r) => r.data);
export const getFollowupStatusMap = async (y, m) => {
  const { data } = await api.get("/operations/followups/status-map", { params: { year: y, month: m } });
  return data || {};
};
export const generateFollowup = (payload) =>
  api.post("/operations/followups/generate", payload).then((r) => r.data);
export const removeFollowup = (id) =>
  api.delete(`/operations/followups/${id}`).then((r) => r.data);
// eslint-disable-next-line no-unused-vars
const _legacyFollowupsKey = FU_KEY;

// ---------------------------------------------------------------------------
// EXTRA COSTS (per cliente/mese)
// ---------------------------------------------------------------------------
const EC_KEY = "aesthetic-os-extra-costs";
const ecKey = (clientId, y, m) => `${clientId}|${y}|${m}`;
// Salvati nel database (Contabilità → Costi come costi occasionali del cliente, IVA inclusa).
// Cache in memoria per letture sincrone; al primo caricamento recupera quelli rimasti nel browser.
const EC_MIGRATED = "aesthetic-os-extra-costs-migrated";
let _ec = null;
let _ecLoading = null;
const ecChanged = () => { try { window.dispatchEvent(new Event("aeos-extra-costs")); } catch { /* noop */ } };
const migrateLocalExtraCosts = async () => {
  if (localStorage.getItem(EC_MIGRATED)) return;
  const local = read(EC_KEY, {});
  const items = [];
  Object.entries(local).forEach(([k, list]) => {
    const [client_id, y, m] = k.split("|");
    (list || []).forEach((x) => items.push({ legacy_id: x.id, client_id, year: Number(y), month: Number(m),
      description: x.description || x.label || x.name || "", amount: Number(x.amount) || 0, notes: x.notes || "",
      added_by: x.added_by, created_at: x.created_at }));
  });
  if (items.length) await api.post("/operations/extra-costs/import", { items });
  localStorage.setItem(EC_MIGRATED, new Date().toISOString());
  // Copia di sicurezza, non più reimportata.
  if (items.length) { localStorage.setItem(`${EC_KEY}-backup`, JSON.stringify(local)); localStorage.removeItem(EC_KEY); }
};
export const hydrateExtraCosts = (force = false) => {
  if (_ec && !force) return Promise.resolve(_ec);
  if (_ecLoading) return _ecLoading;
  _ecLoading = (async () => {
    try {
      await migrateLocalExtraCosts().catch(() => {});
      const { data } = await api.get("/operations/extra-costs");
      _ec = data || [];
    } catch { _ec = _ec || []; } finally { _ecLoading = null; ecChanged(); }
    return _ec;
  })();
  return _ecLoading;
};
export const getExtraCosts = (clientId, y, m) => {
  if (!_ec) { hydrateExtraCosts(); return []; }
  return _ec.filter((c) => c.client_id === clientId && Number(c.year) === Number(y) && Number(c.month) === Number(m));
};
export const extraCostsTotal = (clientId, y, m) => getExtraCosts(clientId, y, m).reduce((s, c) => s + (Number(c.amount) || 0), 0);
export const addExtraCost = async (clientId, y, m, data, author) => {
  const { data: row } = await api.post("/operations/extra-costs", { client_id: clientId, year: y, month: m, ...data, added_by: author || "Operatore" });
  _ec = [...(_ec || []), row]; ecChanged();
  return row;
};
export const removeExtraCost = async (clientId, y, m, id) => {
  await api.delete(`/operations/extra-costs/${id}`);
  _ec = (_ec || []).filter((c) => c.id !== id); ecChanged();
};
export const updateExtraCost = async (clientId, y, m, id, patch) => {
  const { data: row } = await api.patch(`/operations/extra-costs/${id}`, patch);
  _ec = (_ec || []).map((c) => (c.id === id ? row : c)); ecChanged();
  return row;
};

// ---------------------------------------------------------------------------
// GMAIL CONNECTIONS (per utente)
// ---------------------------------------------------------------------------
const GM_KEY = "aesthetic-os-gmail";
export const GMAIL_STATUS = {
  connected: { label: "Connesso", tone: "ok" },
  not_connected: { label: "Non connesso", tone: "neutral" },
  expired: { label: "Scaduto", tone: "warning" },
  error: { label: "Errore", tone: "error" },
};
export const getGmail = (userId) => read(GM_KEY, {})[userId] || { status: "not_connected", connected_email: "", last_connected_at: null };
export const connectGmail = (userId, email) => {
  const store = read(GM_KEY, {});
  store[userId] = { status: "connected", connected_email: email, last_connected_at: now(), last_error: null };
  write(GM_KEY, store);
};
export const disconnectGmail = (userId) => {
  const store = read(GM_KEY, {});
  store[userId] = { status: "not_connected", connected_email: "", last_connected_at: store[userId]?.last_connected_at || null };
  write(GM_KEY, store);
};

export const clientPlan = (clientId) => getRegistryClient(clientId)?.plan;

// ---------------------------------------------------------------------------
// NOTE OPERATIVE (per cliente/mese — storico note interne Monthly Operations)
// ---------------------------------------------------------------------------
const OPN_KEY = "aesthetic-os-op-notes";
// Note operative ora persistite su DB e condivise da tutto il team (endpoint /operations/notes).
export const getOpNotes = async (clientId, y, m) => {
  const { data } = await api.get("/operations/notes", { params: { client_id: clientId, year: y, month: m } });
  return data || [];
};
export const addOpNote = (clientId, y, m, text) =>
  api.post("/operations/notes", { client_id: clientId, year: y, month: m, text }).then((r) => r.data);
export const removeOpNote = (clientId, y, m, id) =>
  api.delete(`/operations/notes/${id}`).then((r) => r.data);
export const updateOpNote = (id, text) =>
  api.patch(`/operations/notes/${id}`, { text }).then((r) => r.data);
export const getOpNotesCounts = async (y, m) => {
  const { data } = await api.get("/operations/notes-counts", { params: { year: y, month: m } });
  return data || {};
};
// eslint-disable-next-line no-unused-vars
const _legacyOpNotesKey = OPN_KEY;

// ---------------------------------------------------------------------------
// NOTE CLIENTE (per cliente — storico note interne, tab Note in Client Detail)
// ---------------------------------------------------------------------------
const CLN_KEY = "aesthetic-os-client-notes";
// Note cliente ora persistite su DB e condivise dal team (endpoint /client-registry/{id}/notes).
// La lettura una tantum del vecchio localStorage serve solo per un'eventuale migrazione manuale.
export const getClientNotes = async (clientId) => {
  const { data } = await api.get(`/client-registry/${clientId}/notes`);
  return (data || []).sort((a, b) => (Number(b.pinned) - Number(a.pinned)) || (new Date(b.created_at) - new Date(a.created_at)));
};
export const addClientNote = (clientId, text, category) =>
  api.post(`/client-registry/${clientId}/notes`, { body: text, category: category || "generale" }).then((r) => r.data);
export const updateClientNote = (clientId, id, patch) =>
  api.patch(`/client-registry/${clientId}/notes/${id}`, patch).then((r) => r.data);
export const removeClientNote = (clientId, id) =>
  api.delete(`/client-registry/${clientId}/notes/${id}`).then((r) => r.data);
// eslint-disable-next-line no-unused-vars
const _legacyClientNotesKey = CLN_KEY;
export const CLIENT_NOTE_CATEGORIES = {
  generale: "Generale",
  strategia: "Strategia",
  operativo: "Operativo",
  commerciale: "Commerciale",
  attenzione: "Da monitorare",
};

// ---------------------------------------------------------------------------
// ETICHETTE STATI (personalizzabili dall'utente — menu a tendina)
// ---------------------------------------------------------------------------
const LBL_KEY = "aesthetic-os-status-labels";
export const ITEM_STATUS_DEFAULT_LABELS = ITEM_STATUS_ORDER.reduce((a, k) => { a[k] = ITEM_STATUS[k].label; return a; }, {});
export const getStatusLabelOverrides = () => read(LBL_KEY, {});
export const saveStatusLabelOverrides = (ov) => write(LBL_KEY, ov || {});
export const itemStatusLabels = () => {
  const ov = getStatusLabelOverrides();
  const out = {};
  ITEM_STATUS_ORDER.forEach((k) => { out[k] = (ov[k] && String(ov[k]).trim()) || ITEM_STATUS[k].label; });
  return out;
};
